import { useState, useEffect } from "react";
import {
  Globe,
  Calendar,
  Video,
  FileText,
  Mail,
  AlertCircle,
  Settings,
  Languages,
  PhoneCall,
  BellRing,
  MessageSquarePlus,
  FolderOpen,
  Send,
} from "lucide-react";
import SkillCard from "../skills/SkillCard";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SchedulingSkillModal from "../skills/SchedulingSkillModal";
import WebSearchModal from "../skills/WebSearchModal";
import {
  WebSearchSettings,
  isWebSearchEnabled,
  convertLegacyWebSearchSetting,
} from "@/types/webSearchSettings";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  SchedulingSkillConfig,
  parseSchedulingConfig,
} from "@/types/scheduling.types";

interface AgentSkillsTabProps {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
}

const AgentSkillsTab = ({ agent, updateAgent }: AgentSkillsTabProps) => {
  const { organization } = useAuth();

  // State for modals
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState(false);
  const [isWebSearchModalOpen, setIsWebSearchModalOpen] = useState(false);

  // Plan features state
  const [planFeatures, setPlanFeatures] = useState<any | null>(null);
  const [featuresLoading, setFeaturesLoading] = useState(true);

  useEffect(() => {
    async function fetchPlanFeatures() {
      if (!organization?.plan_id) {
        setPlanFeatures(null);
        setFeaturesLoading(false);
        return;
      }
      setFeaturesLoading(true);
      const { data, error } = await supabase
        .from("plan_features")
        .select("*")
        .eq("plan_id", organization.plan_id)
        .single();
      if (!error && data) {
        setPlanFeatures(data);
      } else {
        setPlanFeatures(null);
      }
      setFeaturesLoading(false);
    }
    fetchPlanFeatures();
  }, [organization?.plan_id]);

  // Web search skill state
  const webSearchSettings =
    typeof agent.allow_web_search === "string"
      ? convertLegacyWebSearchSetting(agent.allow_web_search)
      : agent.allow_web_search || {
          google_search: false,
          company_website: false,
          links: [],
        };

  // Ensure links is always an array if webSearchSettings is defined
  if (webSearchSettings && !webSearchSettings.links) {
    webSearchSettings.links = [];
  }
  // For backward compatibility during transition, also ensure selected_links exists for the toggle logic, but prioritize links.
  if (webSearchSettings && !webSearchSettings.selected_links) {
    webSearchSettings.selected_links = [];
  }

  const isWebSearchActive = isWebSearchEnabled(webSearchSettings);

  // Scheduling skill state - use proper type parsing
  const schedulingConfig = parseSchedulingConfig(agent.skills_scheduling);
  const isSchedulingEnabled = schedulingConfig?.enabled ?? false;

  // Web search toggle handler
  const handleWebSearchToggle = async (enabled: boolean) => {
    if (enabled) {
      // Open the modal to configure settings when enabling
      setIsWebSearchModalOpen(true);
    } else {
      // Directly disable all web search features
      try {
        const newSettings: WebSearchSettings = {
          google_search: false,
          company_website: false,
          links: [],
          selected_links: [],
        };

        const success = await updateAgent({
          allow_web_search: newSettings,
        });

        if (success) {
          toast.success("Web search disabled");
        } else {
          toast.error("Failed to update web search setting");
        }
      } catch (error) {
        console.error("Error updating web search setting:", error);
        toast.error("An error occurred while updating web search");
      }
    }
  };

  // Save web search settings
  const handleWebSearchSave = async (
    settings: WebSearchSettings,
  ): Promise<boolean> => {
    try {
      const success = await updateAgent({
        allow_web_search: settings,
      });

      if (success) {
        toast.success("Web search settings saved");
      }

      return success;
    } catch (error) {
      console.error("Error saving web search settings:", error);
      return false;
    }
  };

  const handleSchedulingToggle = async (enabled: boolean) => {
    try {
      // If enabling and no configuration exists, open the modal
      if (enabled && !schedulingConfig) {
        setIsSchedulingModalOpen(true);
        return;
      }

      // If enabling, validate that Calendly has a URL
      if (enabled && schedulingConfig?.booking_type === "Calendly") {
        if (
          !schedulingConfig.scheduling_link ||
          schedulingConfig.scheduling_link.trim() === ""
        ) {
          toast.error(
            "Calendly URL is required. Please configure your scheduling settings first.",
          );
          setIsSchedulingModalOpen(true);
          return;
        }
      }

      // If disabling, just update the enabled state
      if (!enabled) {
        const newConfig: SchedulingSkillConfig = {
          ...schedulingConfig,
          enabled: false,
          booking_type: schedulingConfig?.booking_type || "Calendly",
          scheduling_link: schedulingConfig?.scheduling_link || "",
          instructions: schedulingConfig?.instructions || "",
        };

        const success = await updateAgent({
          skills_scheduling: newConfig,
        });

        if (success) {
          toast.success("Scheduling skill disabled");
        } else {
          toast.error("Failed to update scheduling setting");
        }
        return;
      }

      // If enabling and validation passes, update the enabled state
      const newConfig: SchedulingSkillConfig = {
        ...schedulingConfig,
        enabled: true,
        booking_type: schedulingConfig?.booking_type || "Calendly",
        scheduling_link: schedulingConfig?.scheduling_link || "",
        instructions: schedulingConfig?.instructions || "",
      };

      const success = await updateAgent({
        skills_scheduling: newConfig,
      });

      if (success) {
        toast.success("Scheduling skill enabled");
      } else {
        toast.error("Failed to update scheduling setting");
      }
    } catch (error) {
      console.error("Error updating scheduling setting:", error);
      toast.error("An error occurred while updating scheduling");
    }
  };

  const handleSchedulingConfigSave = async (config: SchedulingSkillConfig) => {
    try {
      // Use the enabled state from the config, don't override it
      const success = await updateAgent({
        skills_scheduling: config,
      });

      if (success) {
        toast.success("Scheduling settings saved successfully");
      }

      return success;
    } catch (error) {
      console.error("Error saving scheduling configuration:", error);
      return false;
    }
  };

  // Disabled skill component with badge
  const DisabledSkill = ({
    icon,
    title,
    subtitle,
    comingSoonMessage = "Coming soon",
  }: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    comingSoonMessage?: string;
  }) => (
    <div className="relative h-full">
      <Badge
        className="absolute -top-2 right-6 z-10 bg-gray-500 text-white font-semibold"
        variant="secondary"
      >
        Coming Soon
      </Badge>
      <div className="opacity-75 h-full">
        <SkillCard
          icon={icon}
          title={title}
          subtitle={subtitle}
          enabled={false}
          onToggle={() =>
            toast.info(`${title} skill coming soon: ${comingSoonMessage}`)
          }
          disabled={true}
          vertical={true}
        />
      </div>
    </div>
  );

  // Format web search config section for display
  const renderWebSearchConfigSection = () => {
    const sections = [];

    if (webSearchSettings.google_search) {
      sections.push("Google Search");
    }

    if (webSearchSettings.company_website && organization?.website) {
      sections.push("Company Website");
    }

    if (webSearchSettings.links && webSearchSettings.links.length > 0) {
      sections.push(`${webSearchSettings.links.length} external links`);
    }

    return (
      <div className="flex justify-between items-center mt-3">
        <p className="text-sm text-gray-500">
          {sections.length > 0 ? (
            <span>{sections.join(", ")}</span>
          ) : (
            <span className="text-amber-600">Configuration required</span>
          )}
        </p>
        <Button
          size="sm"
          variant="outline"
          className="flex items-center gap-1"
          onClick={() => setIsWebSearchModalOpen(true)}
        >
          <Settings className="h-4 w-4" />
          Configure
        </Button>
      </div>
    );
  };

  // Render loading state while fetching features
  if (featuresLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-gray-500">Loading plan features...</span>
      </div>
    );
  }

  // Helper booleans for feature gating
  const canWebSearch = !!planFeatures?.can_web_search;
  const canHumanEscalation = !!planFeatures?.can_human_escalation;

  return (
    <div className="space-y-6 py-2">
      <div className="mb-6">
        <p className="text-base text-gray-500">
          Enable skills to enhance your agent's capabilities
        </p>
      </div>

      {/* All Skills Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Web Search Skill */}
        {canWebSearch && (
          <div className={isWebSearchActive ? "relative" : ""}>
            {isWebSearchActive && (
              <Badge
                className="absolute -top-2 right-6 z-10 bg-green-500 text-white font-semibold"
                variant="secondary"
              >
                Active
              </Badge>
            )}
            <div
              className={
                isWebSearchActive
                  ? "ring-1 ring-green-300 rounded-lg h-full"
                  : "h-full"
              }
            >
              <SkillCard
                icon={<Globe className="h-5 w-5" />}
                title="Web Search"
                subtitle="Allow your agent to search the web for up-to-date information beyond its training data"
                enabled={isWebSearchActive}
                onToggle={handleWebSearchToggle}
                vertical={true}
                configSection={
                  isWebSearchActive ? renderWebSearchConfigSection() : undefined
                }
              />
            </div>
          </div>
        )}

        {/* Scheduling Skill */}
        <div className={isSchedulingEnabled ? "relative" : ""}>
          {isSchedulingEnabled && (
            <Badge
              className="absolute -top-2 right-6 z-10 bg-green-500 text-white font-semibold"
              variant="secondary"
            >
              Active
            </Badge>
          )}
          <div
            className={
              isSchedulingEnabled
                ? "ring-1 ring-green-300 rounded-lg h-full"
                : "h-full"
            }
          >
            <SkillCard
              icon={<Calendar className="h-5 w-5" />}
              title="Scheduling"
              subtitle="Let your agent display and book appointments directly in chat using your calendar service"
              enabled={isSchedulingEnabled}
              onToggle={handleSchedulingToggle}
              vertical={true}
              configSection={
                isSchedulingEnabled && (
                  <div className="flex justify-between items-center mt-3">
                    <p className="text-sm text-gray-500">
                      {schedulingConfig?.booking_type ? (
                        <>Type: {schedulingConfig.booking_type}</>
                      ) : (
                        <span className="text-amber-600">
                          Configuration required
                        </span>
                      )}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-1"
                      onClick={() => setIsSchedulingModalOpen(true)}
                    >
                      <Settings className="h-4 w-4" />
                      Configure
                    </Button>
                  </div>
                )
              }
            />
          </div>
        </div>

        {/* File Collection */}
        <DisabledSkill
          icon={<FolderOpen className="h-5 w-5" />}
          title="File Collection"
          subtitle="Allow users to upload and share files with your agent during conversations"
          comingSoonMessage="File upload and sharing capabilities coming soon"
        />

        {/* Send Emails */}
        <DisabledSkill
          icon={<Send className="h-5 w-5" />}
          title="Send Emails"
          subtitle="Your agent can decide to send emails to your users or yourself based on conversation context"
          comingSoonMessage="Smart email sending capabilities coming soon"
        />

        {/* Suggested Replies */}
        <DisabledSkill
          icon={<MessageSquarePlus className="h-5 w-5" />}
          title="Suggested Replies"
          subtitle="Present users with smart reply suggestions to streamline conversation flow"
          comingSoonMessage="AI-powered conversation suggestions coming soon"
        />

        {/* Video */}
        <DisabledSkill
          icon={<Video className="h-5 w-5" />}
          title="Show Video"
          subtitle="Allow your agent to display relevant videos in responses"
          comingSoonMessage="Embed videos from YouTube and other platforms in agent responses"
        />
      </div>

      {/* Modals */}
      <WebSearchModal
        isOpen={isWebSearchModalOpen}
        onClose={() => setIsWebSearchModalOpen(false)}
        initialSettings={webSearchSettings}
        onSave={handleWebSearchSave}
      />

      <SchedulingSkillModal
        isOpen={isSchedulingModalOpen}
        onClose={() => setIsSchedulingModalOpen(false)}
        initialValues={schedulingConfig}
        onSave={handleSchedulingConfigSave}
      />
    </div>
  );
};

export default AgentSkillsTab;
